// Ultra Dark Navy Design System - Manufacturing Executive Psychology Optimized
export const theme = {
  // Legacy font structure for backwards compatibility
  fonts: {
    primary: '"Libre Baskerville", "Baskerville", "Times New Roman", serif',
    secondary: '"Helvetica Neue", "Helvetica", "Arial", sans-serif',
    mono: '"SF Mono", "Monaco", "Inconsolata", "Roboto Mono", monospace'
  },
  
  // Legacy font sizes for backwards compatibility
  fontSizes: {
    xs: '0.875rem',
    sm: '1rem',
    base: '1.125rem',
    lg: '1.25rem',
    xl: '1.375rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
    '6xl': '3.75rem',
    '7xl': '4.5rem'
  },
  
  // Legacy spacing for backwards compatibility
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    base: '1rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
    // Content spacing for sections
    content: {
      narrow: '1rem',
      base: '2rem', 
      wide: '4rem',
      section: '6rem'
    }
  },
  
  // Legacy breakpoints for backwards compatibility
  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px'
  },
  
  // Legacy transitions for backwards compatibility
  transitions: {
    fast: '0.1s ease',
    smooth: '0.2s ease'
  },
  
  // Legacy shadows for backwards compatibility
  shadows: {
    sm: '0 1px 2px rgba(10, 22, 40, 0.05)',
    md: '0 4px 8px rgba(10, 22, 40, 0.12)',
    lg: '0 8px 16px rgba(10, 22, 40, 0.15)',
    xl: '0 12px 24px rgba(10, 22, 40, 0.18)'
  },
  colors: {
    // Primary Brand Colors - Ultra Dark Navy Dominance (60% usage)
    primary: {
      main: '#0A1628',           // Ultra Dark Navy - Primary brand color
      light: '#1A2B42',          // Lighter shade for hover states
      dark: '#050B14'            // Darker shade for depth
    },
    
    // Supporting Colors - Steel Gray (30% usage) 
    secondary: {
      main: '#7F8C8D',           // Steel Gray - Professional supporting color
      light: '#95A5A6',          // Lighter Steel Gray
      dark: '#6C7B7C'            // Darker Steel Gray
    },
    
    // Accent Colors - White/Light (10% usage)
    white: '#FFFFFF',            // Pure White - Clean accents
    accent: {
      gray: '#F8F9FA',           // Accent Gray - Subtle backgrounds
      muted: '#E9ECEF',          // Muted accent for borders
      primary: '#2563EB',        // Blue accent primary
      secondary: '#3B82F6',      // Blue accent secondary
      copper: '#B87333'          // Calibration Copper - Warm metallic accent
    },
    
    // Overlay Colors for gradients and overlays
    overlay: {
      dark: '#0F172A',           // Dark overlay color
      medium: '#1E293B',         // Medium overlay color  
      light: '#334155'           // Light overlay color
    },
    
    // Text Color Hierarchy - Executive Readability
    text: {
      primary: '#1A2B42',         // Ultra Dark Navy for main text
      secondary: '#3A4B57',       // Midpoint for secondary text  
      muted: '#5A6B6C',          // Better contrast for muted text
      light: '#6B7C8D',          // Better contrast for subtle text
      inverse: '#FFFFFF'          // White text for dark backgrounds
    },
    
    // Background System - Manufacturing Clean Aesthetic
    background: {
      primary: '#FFFFFF',         // Pure white primary background
      secondary: '#F8F9FA',       // Accent gray secondary background
      muted: '#E9ECEF',          // Muted background for cards/sections
      dark: '#0A1628',           // Ultra Dark Navy for dark sections
      darkSecondary: '#1A2B42'    // Lighter dark for layered dark sections
    },
    
    // Border System - Professional Definition
    border: {
      light: '#E9ECEF',          // Light borders
      medium: '#7F8C8D',         // Medium contrast borders
      dark: '#0A1628',           // Dark borders for definition
      accent: '#F8F9FA'          // Subtle accent borders
    },
    
    // Status Colors - Executive Decision Making
    status: {
      success: '#28A745',        // Success Green - Clear positive feedback
      successBright: '#00FF7F',  // Bright Success Green - High contrast success
      warning: '#FFC107',        // Professional warning yellow
      error: '#DC3545',          // Clear error red
      info: '#0A1628'            // Info using brand primary
    },
    
    // Interactive States - Manufacturing Precision
    interactive: {
      hover: '#1A2B42',          // Hover state
      active: '#050B14',         // Active state
      disabled: '#95A5A6',       // Disabled state
      focus: '#0A1628'           // Focus outline color
    }
  },
  
  typography: {
    fontFamily: {
      // Baskerville for Brand Name (H1) - Executive Gravitas
      brand: '"Libre Baskerville", "Baskerville", "Times New Roman", serif',
      // Helvetica Neue for All Other Text - Manufacturing Clarity
      primary: '"Helvetica Neue", "Helvetica", "Arial", sans-serif',
      // Monospace for Code/Technical Content
      mono: '"SF Mono", "Monaco", "Inconsolata", "Roboto Mono", monospace'
    },
    
    // Executive Typography Hierarchy - Manufacturing Psychology
    fontSize: {
      xs: '0.875rem',     // 14px - Fine print (lifted from 12px)
      sm: '1rem',         // 16px - Small text (lifted from 14px)
      base: '1.125rem',   // 18px - Body text (lifted from 16px)
      lg: '1.25rem',      // 20px - Large body (lifted from 18px)
      xl: '1.375rem',     // 22px - Small headings (lifted from 20px)
      '2xl': '1.5rem',    // 24px - Medium headings
      '3xl': '1.875rem',  // 30px - Large headings
      '4xl': '2.25rem',   // 36px - Extra large headings
      '5xl': '3rem',      // 48px - Brand name (Baskerville)
      '6xl': '3.75rem',   // 60px - Hero brand name
      '7xl': '4.5rem'     // 72px - Maximum brand impact
    },
    
    // Manufacturing Executive Weight System
    fontWeight: {
      light: 300,         // Light weight for elegance
      normal: 400,        // Normal weight for readability
      medium: 500,        // Medium weight for emphasis
      semibold: 600,      // Semi-bold for subheadings
      bold: 700,          // Bold for headings and impact
      black: 900          // Black weight for maximum impact
    },
    
    // Executive Reading Optimization
    lineHeight: {
      tight: 1.25,        // Tight for headings
      base: 1.5,          // Base for body text
      relaxed: 1.625,     // Relaxed for comfortable reading
      loose: 2            // Loose for maximum readability
    },
    
    // Professional Letter Spacing
    letterSpacing: {
      tighter: '-0.05em',
      tight: '-0.025em',
      normal: '0em',
      wide: '0.025em',
      wider: '0.05em',
      widest: '0.1em'
    }
  },
  
  // Manufacturing Executive Spacing System (Enhanced)
  spacingEnhanced: {
    xs: '0.25rem',      // 4px - Minimal spacing
    sm: '0.5rem',       // 8px - Small spacing
    base: '1rem',       // 16px - Base unit
    lg: '1.5rem',       // 24px - Large spacing
    xl: '2rem',         // 32px - Extra large
    '2xl': '3rem',      // 48px - Section spacing
    '3xl': '4rem',      // 64px - Large section spacing
    '4xl': '6rem',      // 96px - Hero spacing
    '5xl': '8rem',      // 128px - Maximum spacing
    
    // Manufacturing Content Spacing
    content: {
      narrow: '1rem',     // Narrow content padding
      base: '2rem',       // Base content padding
      wide: '4rem',       // Wide content padding
      section: '6rem'     // Section padding
    }
  },
  
  // Executive Device Breakpoints - Manufacturing Precision (Enhanced)
  breakpointsEnhanced: {
    mobile: '480px',      // Mobile devices
    tablet: '768px',      // Tablet devices  
    desktop: '1024px',    // Desktop devices
    wide: '1200px',       // Wide screens
    ultrawide: '1440px'   // Ultra-wide executive displays
  },
  
  // Manufacturing Executive Shadow System (Enhanced)
  shadowsEnhanced: {
    none: 'none',
    sm: '0 1px 2px rgba(10, 22, 40, 0.05)',           // Subtle shadow
    base: '0 2px 4px rgba(10, 22, 40, 0.10)',         // Base shadow
    md: '0 4px 8px rgba(10, 22, 40, 0.12)',           // Medium shadow
    lg: '0 8px 16px rgba(10, 22, 40, 0.15)',          // Large shadow
    xl: '0 12px 24px rgba(10, 22, 40, 0.18)',         // Extra large shadow
    '2xl': '0 20px 40px rgba(10, 22, 40, 0.20)',      // Maximum depth
    
    // Executive Card Shadows
    card: '0 4px 12px rgba(10, 22, 40, 0.08)',
    cardHover: '0 8px 20px rgba(10, 22, 40, 0.12)',
    
    // Manufacturing Focus Shadows
    focus: '0 0 0 3px rgba(10, 22, 40, 0.15)',
    focusInset: 'inset 0 0 0 2px rgba(10, 22, 40, 0.20)'
  },
  
  // Executive Transition System - Manufacturing Precision (Enhanced)
  transitionsEnhanced: {
    fast: '0.1s cubic-bezier(0.4, 0, 0.2, 1)',        // Fast interactions
    base: '0.2s cubic-bezier(0.4, 0, 0.2, 1)',        // Base transitions
    slow: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',        // Slow, deliberate
    slower: '0.5s cubic-bezier(0.4, 0, 0.2, 1)',      // Maximum elegance
    
    // Executive Easing Functions
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',        // Standard easing
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',            // Exit easing
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',             // Enter easing
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)'  // Playful bounce
  },
  
  // Manufacturing Executive Border Radius System
  borderRadius: {
    none: '0',
    sm: '0.125rem',       // 2px - Minimal rounding
    base: '0.25rem',      // 4px - Base rounding
    md: '0.375rem',       // 6px - Medium rounding
    lg: '0.5rem',         // 8px - Large rounding
    xl: '0.75rem',        // 12px - Extra large rounding
    '2xl': '1rem',        // 16px - Maximum professional rounding
    full: '9999px'        // Full rounding for pills/circles
  },
  
  // Executive Z-Index System - Manufacturing Hierarchy
  zIndex: {
    base: 1,              // Base layer
    dropdown: 1000,       // Dropdown menus
    sticky: 1020,         // Sticky elements
    fixed: 1030,          // Fixed elements
    backdrop: 1040,       // Modal backdrops
    modal: 1050,          // Modal content
    popover: 1060,        // Popover content
    tooltip: 1070,        // Tooltip content
    toast: 1080,          // Toast notifications
    maximum: 9999         // Maximum z-index
  }
};